/**
 * soft_render — patch a window's DOM from a freshly built tree without dropping what the player is doing
 * (2026-09-25).
 *
 * The windows that re-render on row callbacks and timers (the structure console, the furniture storage, the City
 * console, the Town window's tabs, the contract board) rebuilt their whole body with `replaceChildren()` /
 * `innerHTML`: a name half-typed in the console's NAME field, the caret, the scroll, an open dropdown — all gone every
 * tick, because anyone anywhere had touched a subscribed table. `softPatch(host, fresh)` lays the fresh tree over the
 * live one instead:
 *   · a node whose markup is unchanged (`isEqualNode`) is KEPT — its listeners, its scroll and its field values;
 *   · the FOCUSED field is never replaced (nor refilled), and neither is any ancestor of it: the patch walks the focus
 *     path and replaces only what changed beside it;
 *   · `deep: true` — for windows whose listeners are DELEGATED to the body (the City console) — morphs every same-tag
 *     pair in place, so a scroller keeps its offset while its rows change. Windows that bind listeners on the elements
 *     themselves stay shallow (a kept element keeps the closure it was built with; only identical markup is kept);
 *   · a field the player has NOT touched follows the data (its rendered value is remembered); one they edited keeps its
 *     text, and a replacement carries it over by `data-k`;
 *   · a `data-soft-keep="<sig>"` island (a form whose own listeners rewrite it) is kept while its signature holds;
 *   · a replaced element's scrolled parts (`[style*="overflow"]`) keep their offsets.
 * Buttons that read a field should look it up by `data-k` at click time (`fieldByKey`) — a kept field and a
 * replaced button would otherwise disagree about which input is "the" input.
 */

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** The value each field was last RENDERED with — a field whose value still equals it is untouched by the player. */
const rendered = new WeakMap<Element, string>();

const isElement = (n: Node): n is Element => n.nodeType === 1;
function isField(n: Node): n is Field {
  if (!isElement(n)) return false;
  const t = n.tagName;
  return t === 'INPUT' || t === 'SELECT' || t === 'TEXTAREA';
}
function isToggle(f: Field): boolean { return f.tagName === 'INPUT' && ((f as HTMLInputElement).type === 'checkbox' || (f as HTMLInputElement).type === 'radio'); }
function fieldValue(f: Field): string { return isToggle(f) ? ((f as HTMLInputElement).checked ? '1' : '0') : f.value; }
function setFieldValue(f: Field, v: string): void { if (isToggle(f)) (f as HTMLInputElement).checked = v === '1'; else f.value = v; }
function fieldsIn(n: Node): Field[] {
  if (!isElement(n)) return [];
  const out: Field[] = isField(n) ? [n] : [];
  for (const f of n.querySelectorAll<Field>('input,select,textarea')) out.push(f);
  return out;
}
/** Remember what a freshly inserted subtree's fields were rendered with. */
function record(n: Node): void { for (const f of fieldsIn(n)) rendered.set(f, fieldValue(f)); }

/** A kept node's untouched fields follow the fresh render; edited ones (and the focused one) keep the player's value. */
function refresh(o: Node, n: Node, focus: Element | null): void {
  const a = fieldsIn(o), b = fieldsIn(n);
  if (a.length !== b.length) return;
  for (let i = 0; i < a.length; i++) refreshOne(a[i], b[i], focus);
}
function refreshOne(f: Field, twin: Field, focus: Element | null): void {
  if (f === focus) return;
  const was = rendered.get(f);
  const want = fieldValue(twin);
  if (was === undefined || fieldValue(f) === want) { rendered.set(f, fieldValue(f)); return; } // unknown, or the edit has landed
  if (fieldValue(f) !== was) return; // the player's edit stands
  if (want !== was) setFieldValue(f, want);
  rendered.set(f, want);
}

/** A deep-morphed field: the attribute sync (a `value` default, an option's `selected`) may have moved it — an edited
 *  field gets the player's value back, an untouched one takes the fresh render's. */
function settleField(f: Field, twin: Field, before: string): void {
  const was = rendered.get(f);
  const want = fieldValue(twin);
  if (was !== undefined && before !== was && before !== want) { if (fieldValue(f) !== before) setFieldValue(f, before); return; }
  if (fieldValue(f) !== want) setFieldValue(f, want);
  rendered.set(f, want);
}

/** Copy `n`'s attributes onto `o`. `keepValue`: a field's `value` / `checked` are its state, not markup — skipped. */
function syncAttrs(o: Element, n: Element, keepValue: boolean): void {
  const skip = (name: string): boolean => keepValue && (name === 'value' || name === 'checked');
  for (const a of Array.from(o.attributes)) if (!n.hasAttribute(a.name) && !skip(a.name)) o.removeAttribute(a.name);
  for (const a of Array.from(n.attributes)) {
    if (skip(a.name)) continue;
    if (o.getAttribute(a.name) !== a.value) o.setAttribute(a.name, a.value);
  }
}

function keepSig(n: Node): string | null { return isElement(n) ? n.getAttribute('data-soft-keep') : null; }

/** Scroll offsets of a subtree's scrollers, by child-index path. */
function scrollState(o: Node): Array<{ path: number[]; top: number; left: number }> {
  if (!isElement(o)) return [];
  const out: Array<{ path: number[]; top: number; left: number }> = [];
  const cands: Element[] = [o, ...Array.from(o.querySelectorAll('[style*="overflow"]'))];
  for (const e of cands) {
    if (e.scrollTop === 0 && e.scrollLeft === 0) continue;
    const path: number[] = [];
    for (let c: Element | null = e; c && c !== o; c = c.parentElement) path.unshift(Array.prototype.indexOf.call(c.parentElement?.children ?? [], c));
    out.push({ path, top: e.scrollTop, left: e.scrollLeft });
  }
  return out;
}
function applyScroll(n: Node, st: Array<{ path: number[]; top: number; left: number }>): void {
  if (!isElement(n)) return;
  for (const s of st) {
    let e: Element | undefined = n;
    for (const i of s.path) { e = e?.children[i]; if (!e) break; }
    if (e) { e.scrollTop = s.top; e.scrollLeft = s.left; }
  }
}

/** The player's edits in a subtree that a replacement can carry: keyed (`data-k`), changed from what was rendered. */
function editsIn(o: Node): Array<{ k: string; tag: string; v: string }> {
  const out: Array<{ k: string; tag: string; v: string }> = [];
  for (const f of fieldsIn(o)) {
    const k = f.getAttribute('data-k');
    const was = rendered.get(f);
    if (k && was !== undefined && fieldValue(f) !== was) out.push({ k, tag: f.tagName, v: fieldValue(f) });
  }
  return out;
}
function applyEdits(n: Node, edits: Array<{ k: string; tag: string; v: string }>): void {
  for (const e of edits) {
    const twin = fieldsIn(n).find((f) => f.getAttribute('data-k') === e.k && f.tagName === e.tag);
    if (twin) setFieldValue(twin, e.v); // `rendered` keeps the fresh default, so the carried text still counts as an edit
  }
}

function replace(o: ChildNode, n: ChildNode): ChildNode {
  const st = scrollState(o);
  const edits = editsIn(o);
  o.replaceWith(n);
  record(n);
  applyEdits(n, edits);
  applyScroll(n, st);
  return n;
}

/** Lay `n` over `o`; returns the node that stands in `o`'s place afterwards (`o` kept or morphed, else `n`). */
function patchPair(o: ChildNode, n: ChildNode, deep: boolean, focus: Element | null): ChildNode {
  const ko = keepSig(o);
  if (ko !== null && ko === keepSig(n)) return o; // the player's island
  if (o.isEqualNode(n)) { refresh(o, n, focus); return o; }
  const same = o.nodeType === n.nodeType && o.nodeName === n.nodeName;
  const holds = !!focus && (o === focus || o.contains(focus));
  if (same && !isElement(o)) { (o as CharacterData).data = (n as CharacterData).data; return o; }
  if (same && (deep || holds)) {
    const oe = o as Element, ne = n as Element;
    if (oe === focus && isField(oe)) { syncAttrs(oe, ne, true); return o; } // never replace — nor refill — the field being typed in
    const before = isField(oe) ? fieldValue(oe) : null;
    syncAttrs(oe, ne, isField(oe));
    patchChildren(oe, ne, deep, focus);
    if (before !== null) settleField(oe as Field, ne as Field, before);
    return o;
  }
  if (holds) return o; // a different kind of node where the focused field stands: keep the field
  return replace(o, n);
}

/** A list row's identity (`data-row`): rows pair by it, so a row removed above the one being typed in never morphs
 *  the next row's data into the kept field's row. Unkeyed children pair in order. */
function rowKey(n: Node): string | null { return isElement(n) ? n.getAttribute('data-row') : null; }

function patchChildren(op: ParentNode & Node, np: ParentNode & Node, deep: boolean, focus: Element | null): void {
  const olds = Array.from(op.childNodes), news = Array.from(np.childNodes);
  const byKey = new Map<string, ChildNode>();
  const loose: ChildNode[] = [];
  for (const o of olds) { const k = rowKey(o); if (k !== null && !byKey.has(k)) byKey.set(k, o); else if (k === null) loose.push(o); }
  const used = new Set<ChildNode>();
  const plan: ChildNode[] = [];
  const inserted: ChildNode[] = [];
  let li = 0;
  for (const n of news) {
    const k = rowKey(n);
    let o: ChildNode | undefined = k !== null ? byKey.get(k) : loose[li++];
    if (o && used.has(o)) o = undefined;
    if (o) { used.add(o); plan.push(patchPair(o, n, deep, focus)); }
    else { plan.push(n); inserted.push(n); }
  }
  for (const o of olds) if (!used.has(o) && o.parentNode === op) o.remove();
  // Order the children as planned, never MOVING the focus holder (a moved node loses its focus): what stands in its
  // way goes to the back and is placed again when its own turn comes.
  for (let i = 0; i < plan.length; i++) {
    const want = plan[i];
    if (op.childNodes[i] === want) continue;
    if (focus && want.parentNode === op && (want === focus || want.contains(focus))) {
      while (op.childNodes[i] !== want) op.appendChild(op.childNodes[i]);
      continue;
    }
    op.insertBefore(want, op.childNodes[i] ?? null);
  }
  for (const n of inserted) record(n);
}

/** Lay `fresh`'s children over `host`'s (see the header). `fresh` is consumed. */
export function softPatch(host: Element, fresh: ParentNode & Node, opts: { deep?: boolean } = {}): void {
  const a = document.activeElement;
  const focus = a && a !== document.body && host.contains(a) ? a : null;
  patchChildren(host, fresh, !!opts.deep, focus);
}

/** Build into a scratch element of the host's tag, then patch the host from it. */
export function softRender(host: HTMLElement, build: (scratch: HTMLElement) => void, opts: { deep?: boolean } = {}): void {
  const scratch = document.createElement(host.tagName.toLowerCase());
  build(scratch);
  softPatch(host, scratch, opts);
}

/** The live field under `root` keyed `data-k="<k>"` (the one on screen — a click reads what the player typed). */
export function fieldByKey<T extends Field = HTMLInputElement>(root: ParentNode | null | undefined, k: string): T | null {
  if (!root) return null;
  for (const f of root.querySelectorAll<T>('[data-k]')) if (f.getAttribute('data-k') === k) return f;
  return null;
}
